/**
 * RenderingEventHandler
 *
 * Manages all event subscriptions for the ElementRenderingEngine.
 * Extracted from ElementRenderingEngine for single-responsibility.
 */

import { devLog } from '../../../../../../utils/devLog';
import { globalEventStream } from '../../core/EventStream';
import type { D3PlanetSpriteManager } from '../../animation';

/**
 * Callbacks the engine provides for event handling
 */
export interface RenderingEventCallbacks {
    initializeD3SpriteSystem: () => void;
    initializeSecondaryDataSpriteSystem: () => void;
    processDeferredSprites: () => void;
    handleChartDataClear: (event: any, chartId?: string) => void;
    handleSecondaryDataPositioning: (event: any) => void;
    updatePlanetPositionsFromDrag: (allPlanets: any, targetSpriteManager?: D3PlanetSpriteManager) => void;
    getSpriteManagerForChart: (chartId: string) => D3PlanetSpriteManager | undefined;
}

/**
 * Manages event subscriptions for the rendering engine
 */
export class RenderingEventHandler {
    private subscriptions: { unsubscribe: () => void }[] = [];

    constructor(private callbacks: RenderingEventCallbacks) {}

    /**
     * Setup all event subscriptions
     */
    public setupEventSubscriptions(): void {
        this.setupCoreSubscriptions();
        this.setupSecondaryDataIntegration();
    }

    /**
     * Setup core event subscriptions for chart data management
     */
    private setupCoreSubscriptions(): void {
        // Subscribe to lines.ready events to initialize sprite system AFTER real .lines-overlay exists
        const linesReadySubscription = globalEventStream.subscribe('lines.ready', (_event: any) => {
            // Initialize sprite system AFTER real .lines-overlay is created and degree lines are rendered
            this.callbacks.initializeD3SpriteSystem();

            // Initialize secondary data sprite system
            this.callbacks.initializeSecondaryDataSpriteSystem();

            // Process deferred sprite creation now that lines are ready and sprite system is initialized
            this.callbacks.processDeferredSprites();
        });

        this.subscriptions.push(linesReadySubscription);

        // Subscribe to chart clear events
        const clearSubscription = globalEventStream.subscribe('chart.data.cleared', (event: any) => {
            const eventChartId = event?.metadata?.chartId || event?.chartId;

            // Only process if we have a sprite manager for this chart
            // This prevents cross-chart interference in multi-chart scenarios
            if (eventChartId && !this.callbacks.getSpriteManagerForChart(eventChartId)) {
                return; // Not our chart, ignore
            }

            devLog(`[ElementRenderingEngine] Received chart.data.cleared, chartId: ${eventChartId || 'unknown'}`);
            this.callbacks.handleChartDataClear(event, eventChartId);
        });

        this.subscriptions.push(clearSubscription);

        // Subscribe to planet drag move events for real-time position updates
        const dragMoveSubscription = globalEventStream.subscribe('planet.drag.move', (event: any) => {
            const chartId = event?.metadata?.chartId;
            if (!chartId) return;

            // Get sprite manager for the specific chart (not just currentChartId)
            const targetSpriteManager = this.callbacks.getSpriteManagerForChart(chartId);
            if (!targetSpriteManager) return;

            const allPlanets = event?.metadata?.allPlanets;
            if (allPlanets) {
                this.callbacks.updatePlanetPositionsFromDrag(allPlanets, targetSpriteManager);
            }
        });

        this.subscriptions.push(dragMoveSubscription);

        // Subscribe to time motion move events for real-time position updates
        const timeMoveSubscription = globalEventStream.subscribe('time.motion.move', (event: any) => {
            const chartId = event?.metadata?.chartId;
            if (!chartId) return;

            const targetSpriteManager = this.callbacks.getSpriteManagerForChart(chartId);
            if (!targetSpriteManager) return;

            const allPlanets = event?.metadata?.allPlanets;
            if (allPlanets) {
                this.callbacks.updatePlanetPositionsFromDrag(allPlanets, targetSpriteManager);
            }
        });

        this.subscriptions.push(timeMoveSubscription);
    }

    /**
     * Setup secondary data integration subscriptions
     */
    private setupSecondaryDataIntegration(): void {
        const positioningSubscription = globalEventStream.subscribe('secondary.data.ready.for.positioning', (event: any) => {
            try {
                this.callbacks.handleSecondaryDataPositioning(event);
            } catch (_error) {
                // Silently handle errors
            }
        });

        this.subscriptions.push(positioningSubscription);
    }

    /**
     * Clean up all event subscriptions
     */
    public destroy(): void {
        this.subscriptions.forEach(subscription => {
            try {
                subscription.unsubscribe();
            } catch (_error) {
                // Silently handle errors
            }
        });

        this.subscriptions.length = 0;
    }
}
